import { CalendarCheck, ArrowRight, Check } from "lucide-react";
import { CLINIC } from "../data/content";
import { Link } from "react-router-dom";
import SuccessSpine from "../components/site/SuccessSpine";
import { useBookingFlow } from "../hooks/useBookingFlow";
import { StepIndicator, StepReason, StepDateTime, StepContact } from "../components/book/BookingSteps";

export function BookAppointment() {
  const { step, form, set, loading, done, canGoNext, next, back, submit } = useBookingFlow();

  if (done) {
    return (
      <div className="container-wide py-24 max-w-xl text-center" data-testid="book-success">
        <div className="flex justify-center">
          <SuccessSpine size={160} />
        </div>
        <h1 className="font-heading text-3xl text-brand-ink mt-6">Appointment request received</h1>
        <p className="text-brand-ink2 mt-3">
          Thank you, {form.name || "friend"}. Our team will confirm your appointment shortly. This is an appointment request — please expect a confirmation call or WhatsApp from us.
        </p>
        <div className="flex gap-3 justify-center mt-6">
          <Link to="/" className="btn-primary" data-testid="success-home">Back to home</Link>
          <a href={`https://wa.me/${CLINIC.whatsapp}`} target="_blank" rel="noreferrer" className="btn-outline">WhatsApp us</a>
        </div>
      </div>
    );
  }

  return (
    <div className="container-wide py-16 max-w-3xl">
      <div className="eyebrow mb-3">Book an appointment</div>
      <h1 className="font-heading text-4xl md:text-5xl text-brand-ink leading-[1.05]">
        A few short steps to your first assessment.
      </h1>
      <p className="text-brand-ink2 mt-4">
        This is an appointment request. Our team confirms after receiving your details.
      </p>

      <StepIndicator step={step} />

      <div className="card-soft p-6 md:p-8 mt-8">
        {step === 1 && <StepReason value={form.reason} onChange={(v) => set("reason", v)} />}
        {step === 2 && <StepDateTime form={form} set={set} />}
        {step === 3 && <StepContact form={form} set={set} />}

        <div className="mt-6 flex justify-between">
          {step > 1 ? (
            <button onClick={back} className="btn-outline" data-testid="book-back">Back</button>
          ) : <span />}
          {step < 3 ? (
            <button
              disabled={!canGoNext()}
              onClick={next}
              className="btn-primary disabled:opacity-50"
              data-testid="book-next"
            >
              Continue <ArrowRight size={16} />
            </button>
          ) : (
            <button disabled={loading} onClick={submit} className="btn-primary" data-testid="book-submit">
              <CalendarCheck size={16} /> {loading ? "Sending…" : "Request Appointment"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function Contact() {
  return (
    <div className="container-wide py-16 grid md:grid-cols-2 gap-10">
      <div>
        <div className="eyebrow mb-3">Get in touch</div>
        <h1 className="font-heading text-4xl md:text-6xl text-brand-ink leading-[1.05]">
          We're here to help you take the first step.
        </h1>
        <div className="mt-6 space-y-2 text-brand-ink2">
          <div><strong className="text-brand-ink">Address:</strong> {CLINIC.address}</div>
          <div><strong className="text-brand-ink">Phone:</strong> <a href={`tel:${CLINIC.phoneRaw}`} className="text-brand-terracotta">{CLINIC.phone}</a></div>
          <div><strong className="text-brand-ink">WhatsApp:</strong> <a href={`https://wa.me/${CLINIC.whatsapp}`} className="text-brand-terracotta">{CLINIC.phone}</a></div>
          <div><strong className="text-brand-ink">Email:</strong> <a href={`mailto:${CLINIC.email}`} className="text-brand-terracotta">{CLINIC.email}</a></div>
          <div><strong className="text-brand-ink">Hours:</strong> Mon–Sat 9am–8pm · Sun 10am–2pm</div>
        </div>
        <div className="mt-6 flex gap-3">
          <Link to="/book" className="btn-primary">Book Appointment</Link>
          <a href={`https://wa.me/${CLINIC.whatsapp}`} target="_blank" rel="noreferrer" className="btn-outline">WhatsApp us</a>
        </div>
      </div>
      <div className="card-soft overflow-hidden aspect-[4/3]">
        <iframe
          title="Physio Hyve location"
          src={`https://www.google.com/maps?q=${CLINIC.mapQuery}&output=embed`}
          className="w-full h-full border-0"
          loading="lazy"
        />
      </div>
    </div>
  );
}
